import { apiRequest } from './authService';

/**
 * Gold Purchase ledger service.
 * Rows are grouped by day (YYYY-MM-DD). Cash spent is per-day; gold grams
 * accumulate across days with 'set'/'subtract' adjustments.
 */

/**
 * Get everything for one day: rows, adjustments, carried gold opening, default rate.
 * @param {string} date YYYY-MM-DD
 */
export async function getDay(date) {
  try {
    const response = await apiRequest('gold-purchase/day', {
      method: 'POST',
      body: JSON.stringify({ date })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to load day' };
  } catch (e) {
    console.error('getDay error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Lightweight change signal for a day (for polling-based live sync).
 * @param {string} date
 * @returns {Promise<{success:boolean, data?:{version:string}}>}
 */
export async function getDayVersion(date) {
  try {
    const response = await apiRequest('gold-purchase/day-version', {
      method: 'POST',
      body: JSON.stringify({ date })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data };
    }
    return { success: false };
  } catch (e) {
    // Silent — polling failures shouldn't spam the console/UI.
    return { success: false };
  }
}

/**
 * Find a purchase by note number (exact or partial). Returns the row + its
 * purchase_date so the UI can jump to that day and highlight it.
 * @param {string} note
 */
export async function findByNote(note) {
  try {
    const response = await apiRequest('gold-purchase/find-note', {
      method: 'POST',
      body: JSON.stringify({ note })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Not found' };
  } catch (e) {
    console.error('findByNote error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/** Fetch central settings (default rate, note seq). */
export async function getSettings() {
  try {
    const response = await apiRequest('gold-purchase/settings', { method: 'POST' });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to load settings' };
  } catch (e) {
    console.error('getSettings error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/** Set the central default USD→SRD rate. */
export async function setDefaultRate(rate) {
  try {
    const response = await apiRequest('gold-purchase/set-rate', {
      method: 'POST',
      body: JSON.stringify({ rate })
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to set rate' };
  } catch (e) {
    console.error('setDefaultRate error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/** Create a new empty purchase row for a day (server reserves note number). */
export async function addRow(date) {
  try {
    const response = await apiRequest('gold-purchase/add-row', {
      method: 'POST',
      body: JSON.stringify({ date })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to add row' };
  } catch (e) {
    console.error('addRow error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Update a purchase row. Server recomputes totals.
 * @param {number|string} id
 * @param {{grams?, price_per_gram?, rate_srd?, paid_usd?, paid_srd?, paid_eur?}} fields
 */
export async function updateRow(id, fields) {
  try {
    const response = await apiRequest('gold-purchase/update-row', {
      method: 'POST',
      body: JSON.stringify({ id, ...fields })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to update row' };
  } catch (e) {
    console.error('updateRow error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

export async function deleteRow(id) {
  try {
    const response = await apiRequest('gold-purchase/delete-row', {
      method: 'POST',
      body: JSON.stringify({ id })
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to delete row' };
  } catch (e) {
    console.error('deleteRow error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Add a gold adjustment for a day.
 * @param {string} date
 * @param {'set'|'subtract'} type
 * @param {number} amount grams
 */
export async function addAdjustment(date, type, amount) {
  try {
    const response = await apiRequest('gold-purchase/add-adjustment', {
      method: 'POST',
      body: JSON.stringify({ date, type, amount })
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to add adjustment' };
  } catch (e) {
    console.error('addAdjustment error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/** Undo (delete) the most recent adjustment on a day. */
export async function undoAdjustment(date) {
  try {
    const response = await apiRequest('gold-purchase/undo-adjustment', {
      method: 'POST',
      body: JSON.stringify({ date })
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to undo adjustment' };
  } catch (e) {
    console.error('undoAdjustment error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}
